import { Check, SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import SIDotsLoader from "app/pages/sales-intelligence/common-components/SIDotsLoader/SIDotsLoader";

import type { CrmEnrichmentJob } from "../../types/types";
import { formatNextRunDate, formatRunDate } from "../../utils/formatRunDate";

import { CrmEnrichmentJobRunStatusWrapper } from "./CrmEnrichmentJobRunStatusWrapper";
import { StyledJobStatusIcon, StyledJobStatusValue } from "./styles";

type Props = Pick<CrmEnrichmentJob, "type" | "status" | "lastRunDate" | "frequencyInMinutes">;

const CrmEnrichmentJobRunStatus = ({ type, status, lastRunDate, frequencyInMinutes }: Props): React.ReactElement => {
    if (!status) {
        return <CrmEnrichmentJobRunStatusWrapper text={translate("si.crm-enrichment.job.status.no-runs")} />;
    }

    if (status === "inProgress" || status === "created") {
        return (
            <CrmEnrichmentJobRunStatusWrapper text={translate("si.crm-enrichment.job.status.running")}>
                <SIDotsLoader />
            </CrmEnrichmentJobRunStatusWrapper>
        );
    }

    if (status === "failed") {
        return (
            <CrmEnrichmentJobRunStatusWrapper text={translate("si.crm-enrichment.job.status.failed")}>
                <SWReactIcons
                    iconColor={colorsPalettes.red["500"]}
                    iconName="clear"
                    size="xs"
                />
            </CrmEnrichmentJobRunStatusWrapper>
        );
    }

    const lastRun = lastRunDate
        ? translate(`si.crm-enrichment.job.status.${type}.last-run`, { datetime: formatRunDate(lastRunDate) })
        : "";
    const nextRun =
        frequencyInMinutes && lastRunDate && type === "scheduled"
            ? ` · ${translate("si.crm-enrichment.job.status.next-run", { datetime: formatNextRunDate(lastRunDate, frequencyInMinutes) })}`
            : "";

    return (
        <CrmEnrichmentJobRunStatusWrapper>
            <StyledJobStatusIcon $color={colorsPalettes.green["500"]}>
                <Check size="sm" />
            </StyledJobStatusIcon>
            {lastRunDate ? (
                <StyledJobStatusValue>
                    ({lastRun}
                    {nextRun})
                </StyledJobStatusValue>
            ) : null}
        </CrmEnrichmentJobRunStatusWrapper>
    );
};

export default CrmEnrichmentJobRunStatus;
